import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { useMounted } from '@app/hooks/useMounted';
import { Space, Tooltip, Button, Popconfirm, Input } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { notificationController } from '@app/controllers/notificationController';
import { CrudSettingsContext } from '@app/contexts/CrudSettingsContext';
import { Pagination } from '@app/models/Pagination';
import { ProductPricingTableRow } from '@app/models/product_pricing/ProductPricingTableRow';
import { getProductPricingTableData } from '@app/api/productPricing.api';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

type ProductPricingTableProps = {
  product_category_id: number | null;
  product_id: number | null;
  product_variety_id: number | null;
};

export const ProductPricingTable: React.FC<ProductPricingTableProps> = ({
  product_category_id,
  product_id,
  product_variety_id,
}) => {
  const [tableData, setTableData] = useState<{
    data: ProductPricingTableRow[];
    pagination: Pagination;
    loading: boolean;
  }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const { t } = useTranslation();
  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const [fieldsChanged, setFieldsChanged] = useState(false);
  const [form] = BaseButtonsForm.useForm();

  const fetch = useCallback(
    (pagination: Pagination) => {
      setTableData((tableData) => ({ ...tableData, loading: true }));
      getProductPricingTableData({
        ...pagination,
        search: form.getFieldValue('search'),
        product_category_id: product_category_id,
        product_id: product_id,
        product_variety_id: product_variety_id,
      })
        .then((res) => {
          if (isMounted.current) {
            setTableData({
              data: res.data,
              pagination: {
                current: res.pagination.current,
                pageSize: res.pagination.pageSize,
                total: res.pagination.total,
              },
              loading: false,
            });
          }
        })
        .catch((error: any) => {
          setTableData((tableData) => ({ ...tableData, loading: false }));
          notificationController.error({
            message: error.message,
          });
        });
    },
    [isMounted],
  );

  useEffect(() => {
    fetch(initialPagination);
  }, [fetch]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
    });
  };

  const handleViewRow = (id: number) => {
    navigate('/view-product-pricing', {
      state: {
        id: id,
        product_category_id: product_category_id,
        product_id: product_id,
        product_variety_id: product_variety_id,
      },
    });
  };

  const handleEditRow = (id: number) => {
    navigate('/edit-product-pricing', {
      state: {
        id: id,
        product_category_id: product_category_id,
        product_id: product_id,
        product_variety_id: product_variety_id,
      },
    });
  };

  const handleDeleteRow = (id: number) => {
    //delete row
  };

  const handleCreate = () => {
    navigate('/add-product-pricing', {
      state: {
        product_category_id: product_category_id,
        product_id: product_id,
        product_variety_id: product_variety_id,
      },
    });
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span
        style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}
      >{`Showing ${range[0]} - ${range[1]} of ${total}`}</span>
    );
  };

  const columns: ColumnsType<ProductPricingTableRow> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: '30%',
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: 'Pricing Strategy',
      dataIndex: 'pricing_strategy',
      key: 'pricing_strategy',
    },
    {
      title: 'Model Type',
      dataIndex: 'model_type',
      key: 'model_type',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '30%',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record.id)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>

            <Tooltip title="Edit">
              <Button
                type="text"
                onClick={() => handleEditRow(record.id)}
                icon={<EditOutlined />}
                style={{ color: '#52c41a' }}
              />
            </Tooltip>

            <Tooltip title="Delete">
              <Popconfirm
                title="Are you sure to delete this role?"
                onConfirm={() => handleDeleteRow(record.id)}
                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              >
                <Button type="text" danger icon={<DeleteOutlined />} style={{ color: 'red' }} />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];
  return (
    <>
      <Space style={{ marginBottom: '16px', justifyContent: 'space-between', width: '100%' }}>
        <BaseButton icon={<PlusOutlined />} type="primary" onClick={handleCreate} size="small">
          {t('Add New Product Pricing')}
        </BaseButton>
        <BaseButtonsForm
          form={form}
          layout="inline"
          isFieldsChanged={fieldsChanged}
          setFieldsChanged={setFieldsChanged}
        >
          <BaseButtonsForm.Item name={'search'}>
            <Input
              size="middle"
              suffix={
                <Button
                  icon={<SearchOutlined />}
                  size="middle"
                  type="text"
                  onClick={() => {
                    fetch(initialPagination);
                  }}
                />
              }
              placeholder={'Search'}
            />
          </BaseButtonsForm.Item>
          <Button
            type="ghost"
            size="middle"
            icon={<ReloadOutlined />}
            onClick={() => {
              form.resetFields(['search']);
              fetch(initialPagination);
            }}
          />
        </BaseButtonsForm>
      </Space>
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </>
  );
};
